import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Transition } from '@headlessui/react';
import { tv, type VariantProps } from 'tailwind-variants';
import { useOnClickOutside } from 'usehooks-ts';

function tooltipPosition(
  { height, width, left, top }: DOMRect,
  position: 'top' | 'bottom' | 'left' | 'right',
): [number, number] {
  switch (position) {
    case 'top': {
      const x = left + width / 2;
      const y = top;

      return [x, y];
    }
    case 'bottom': {
      const x = left + width / 2;
      const y = top + height;

      return [x, y];
    }
    case 'left': {
      const x = left;
      const y = top + height / 2;

      return [x, y];
    }
    case 'right': {
      const x = left + width;
      const y = top + height / 2;

      return [x, y];
    }
  }
}

export type TooltipProps = VariantProps<typeof tooltipStyle> & {
  content: ReactNode;
  className?: string;
  delay?: boolean | number;
  clickToKeepOpen?: boolean;
  closeOnMouseLeave?: boolean;
};

export function Tooltip({
  children,
  content,
  className,
  position = 'top',
  delay = false,
  clickToKeepOpen = false,
  closeOnMouseLeave = false,
  hide = false,
}: React.PropsWithChildren<TooltipProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(document.createElement('div'));

  const [focusCoord, setFocusCoord] = useState<DOMRect | undefined>(undefined);
  const [clickCoord, setClickCoord] = useState<DOMRect | undefined>(undefined);

  const coord = clickToKeepOpen && clickCoord ? clickCoord : focusCoord;

  const style = tooltipStyle({ position, hide });

  useEffect(
    // Mount the div on body
    function () {
      const appened = document.body.appendChild(tooltipRef.current);

      return function () {
        document.body.removeChild(appened);
      };
    },
    [tooltipRef],
  );

  function handleMouseOver(e: MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setFocusCoord(rect);
  }
  function handleMouseLeave() {
    setFocusCoord(undefined);
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setClickCoord(rect);
  }

  function handleTooltipMouseLeave() {
    if (closeOnMouseLeave && clickToKeepOpen) {
      setClickCoord(undefined);
    }
  }

  useOnClickOutside(tooltipRef, function () {
    setClickCoord(undefined);
  });

  const [x, y] = useMemo<[number, number]>(
    () => (coord ? tooltipPosition(coord, position) : [0, 0]),
    [coord, position],
  );

  const tooltip = createPortal(
    <Transition
      show={Boolean(coord)}
      as="div"
      className={style.tooltip()}
      enter="transition ease-in duration-200 transform opacity-0"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition ease-out duration-150 transform opacity-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      style={{
        left: x,
        top: y,
        zIndex: coord ? 99999 : -99999,
        transitionDelay: `${resolveDelay(delay)}ms`,
      }}
      onMouseLeave={handleTooltipMouseLeave}
    >
      <div className={style.tooltipInner({ className })}>{content}</div>
    </Transition>,
    tooltipRef.current,
  );

  return (
    <>
      <div
        className={style.container()}
        ref={containerRef}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {children}
      </div>
      {tooltip}
    </>
  );
}

const TOOLTIP_DEFAULT_DELAY = 500;

function resolveDelay(delay: boolean | number): number {
  if (typeof delay === 'boolean') {
    return delay ? TOOLTIP_DEFAULT_DELAY : 0;
  }

  return delay;
}

const tooltipStyle = tv({
  slots: {
    container: 'inline-block',
    tooltip: 'absolute',
    tooltipInner: `
      absolute
      w-max
      rounded
      bg-gray-800
      px-3
      py-2
      text-left
      text-sm
      text-gray-200
    `,
  },
  variants: {
    hide: {
      true: {
        tooltip: 'hidden',
      },
      false: {},
    },
    position: {
      top: {
        tooltipInner: `
          bottom-1
          left-0
          -translate-x-1/2
        `,
      },
      bottom: {
        tooltipInner: `
          left-0
          top-1
          -translate-x-1/2
        `,
      },
      left: {
        tooltipInner: `
          right-1
          top-0
          -translate-y-1/2
        `,
      },
      right: {
        tooltipInner: `
          left-1
          top-0
          -translate-y-1/2
        `,
      },
    },
  },
});
